import axios from 'axios';

export type AuthTokens = {
  access: string;
  refresh?: string;
};

export type ApiUser = {
  id: number;
  username: string;
  email: string;
  firstName?: string;
  lastName?: string;
  isStaff?: boolean;
  first_name?: string;
  last_name?: string;
  is_staff?: boolean;
  is_active?: boolean;
  can_batch_export?: boolean;
  canBatchExport?: boolean;
};

export type AddressComponent = {
  long_name?: string;
  short_name?: string;
  types?: string[];
};

export type ReportSummaryCard = {
  label: string;
  count: number;
  range_km?: number | null;
  radius_km?: number | null;
  display_radius_km?: number | null;
  display_radius_label?: string | null;
  nearest?: {
    name?: string;
    distance_km?: number;
  } | null;
};

export type ReportScoreMap = Record<string, number | null | undefined>;

export type ReportInsight = {
  title: string;
  detail?: string;
};

export type HazardWindSegment = {
  label: string;
  percent: number;
  color?: string;
};

export type HazardWindBand = {
  month: string;
  segments?: HazardWindSegment[];
};

export type HazardWind = {
  stationName: string;
  span?: string;
  distanceKm: number;
  mean: number;
  p95: number;
  max: number;
  zone?: string;
  zoneNarrative?: string;
  dominant?: string[];
  bands?: HazardWindBand[];
};

export type HazardWildfire = {
  riskLevel: number;
  riskLabel: string;
  distanceKm: number;
  areaSqM?: number | null;
};

export type HazardSeismicBin = {
  label: string;
  count: number;
};

export type HazardSeismicNearest = {
  name?: string | null;
  distance_km?: number | null;
  magnitude?: number | null;
  intensity?: number | null;
  event_date?: string | null;
};

export type HazardSeismic = {
  bins?: HazardSeismicBin[];
  nearby_count?: number;
  recent_decade?: number;
  nearest?: HazardSeismicNearest | null;
};

export type HazardNarratives = {
  wind?: string[];
  windrose?: string[];
  wildfire?: string;
};

export type ReportHazard = {
  label?: string;
  latitude: number;
  longitude: number;
  wind?: HazardWind | null;
  wildfire?: HazardWildfire | null;
  seismic?: HazardSeismic | null;
  narratives?: HazardNarratives | null;
};

export type ReportAnalysis = {
  location: {
    name: string;
    latitude: number;
    longitude: number;
    formatted_address?: string | null;
    postal_code?: string | null;
    municipality?: string | null;
    state?: string | null;
    country?: string | null;
    generated_at?: string | null;
    state_capital?: string | null;
    state_capital_distance_km?: number | null;
  };
  summary_cards: ReportSummaryCard[];
  scores: ReportScoreMap;
  insights: ReportInsight[];
  hazard?: ReportHazard | null;
};

export type AnalyzeLocationResponse = {
  status: 'success';
  analysis: ReportAnalysis;
  html_report: string;
  report_id: string;
  report_url: string;
  hazard_report_url: string;
};

export type AnalyzeLocationPayload = {
  latitude: number;
  longitude: number;
  name?: string;
  formatted_address?: string;
  place_id?: string;
  address_components?: AddressComponent[];
  country_code?: string;
  country?: string;
};

export type BatchExportLocation = {
  name?: string;
  address?: string;
  place_id?: string;
  latitude?: number;
  longitude?: number;
  country_code?: string;
  country?: string;
};

export type SupportedCountry = {
  code: string;
  slug: string;
  name: string;
  active: boolean;
  rollout_status: string;
  scoring_profile?: string;
  dataset_available?: boolean;
  hazard_available?: boolean;
  autocomplete_enabled?: boolean;
};

export type MapsConfigResponse = {
  maps_api_key: string | null;
  active_country_codes: string[];
  supported_countries: SupportedCountry[];
};

export type BatchExportPayload = {
  locations: BatchExportLocation[];
  reverse_geocode?: boolean;
};

export type SavedReport = {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
  formatted_address?: string;
  created_at: string;
  public_url: string;
  hazard_report_url: string;
};

export type SavedReportDetail = SavedReport & {
  place_id?: string;
  analysis: ReportAnalysis;
  html_report: string;
};

export type DeepResearchReport = {
  id: string;
  status: 'queued' | 'running' | 'succeeded' | 'failed';
  model?: string;
  html_report?: string;
  summary?: string;
  citations?: unknown;
  error_message?: string;
  created_at: string;
  updated_at: string;
};

export type AdminSummaryTotals = {
  total_users: number;
  active_users: number;
  staff_users: number;
  total_reports: number;
};

export type AdminSummaryUser = {
  id: number;
  username: string;
  email: string;
  is_staff: boolean;
  is_active: boolean;
  last_login?: string | null;
  last_report_at?: string | null;
  report_count: number;
};

export type AdminSummaryResponse = {
  totals: AdminSummaryTotals;
  users: AdminSummaryUser[];
};

export type AdminReportActivity = SavedReport & {
  user_username: string;
  user_email: string;
};

export type ReportHistoryResponse<T> = {
  results: T[];
  next_cursor: string | null;
  has_more: boolean;
  total_count: number;
};

const TOKEN_STORAGE_KEY = 'gridfield:authTokens';

export const tokenStorage = {
  get(): AuthTokens | null {
    const raw = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as AuthTokens;
    } catch (error) {
      console.warn('Unable to parse stored tokens', error);
      return null;
    }
  },
  set(tokens: AuthTokens | null) {
    if (!tokens) {
      localStorage.removeItem(TOKEN_STORAGE_KEY);
      return;
    }
    localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(tokens));
  }
};

const API_BASE = import.meta.env.VITE_API_BASE ?? '/api';

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json'
  }
});

api.interceptors.request.use((config) => {
  const tokens = tokenStorage.get();
  if (tokens?.access) {
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${tokens.access}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      tokenStorage.set(null);
    }
    return Promise.reject(error);
  }
);

export const authApi = {
  async login(username: string, password: string) {
    const { data } = await api.post<AuthTokens>('/auth/login/', { username, password });
    tokenStorage.set(data);
    return data;
  },
  async fetchCurrentUser() {
    const { data } = await api.get<ApiUser>('/auth/me/');
    return data;
  },
  async registerUser(payload: { username: string; email: string; password: string; role?: 'admin' | 'member' }) {
    const { data } = await api.post<ApiUser>('/auth/register/', payload);
    return data;
  },
  async toggleUserActive(userId: number) {
    const { data } = await api.post<{ id: number; is_active: boolean }>(
      `/auth/users/${String(userId)}/toggle-active/`
    );
    return data;
  },
  logout() {
    tokenStorage.set(null);
  }
};

export const reportsApi = {
  async analyzeLocation(payload: AnalyzeLocationPayload) {
    const { data } = await api.post<AnalyzeLocationResponse>('/reports/analyze-location/', payload);
    return data;
  },
  async batchExport(payload: BatchExportPayload) {
    const response = await api.post('/reports/batch-export/', payload, { responseType: 'blob' });
    const header = response.headers?.['content-disposition'] as string | undefined;
    let filename = 'gridfield-batch-metrics.xlsx';
    if (header) {
      const match = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(header);
      const candidate = match?.[1] ?? match?.[2];
      if (candidate) {
        try {
          filename = decodeURIComponent(candidate);
        } catch {
          filename = candidate;
        }
      }
    }
    return { blob: response.data as Blob, filename };
  },
  async fetchMapsConfig() {
    const { data } = await api.get<MapsConfigResponse>('/reports/maps-api-key/');
    return data;
  },
  async fetchMapsKey() {
    const { data } = await api.get<MapsConfigResponse>('/reports/maps-api-key/');
    return data.maps_api_key ?? null;
  },
  async fetchHistory(params?: { before?: string | null; limit?: number }) {
    const { data } = await api.get<ReportHistoryResponse<SavedReport>>('/reports/history/', {
      params
    });
    return data;
  },
  async fetchAdminHistory(params?: { before?: string | null; limit?: number }) {
    const { data } = await api.get<ReportHistoryResponse<AdminReportActivity>>('/reports/admin/history/', { params });
    return data;
  },
  async fetchReport(reportId: string) {
    const { data } = await api.get<SavedReportDetail>(`/reports/history/${reportId}/`);
    return data;
  },
  async renameReport(reportId: string, payload: { name: string }) {
    const { data } = await api.patch<SavedReportDetail>(`/reports/history/${reportId}/`, payload);
    return data;
  },
  async archiveReport(reportId: string) {
    await api.delete(`/reports/history/${reportId}/`);
  },
  async triggerDeepResearch(reportId: string) {
    const { data } = await api.post<DeepResearchReport>(`/reports/${reportId}/deep-research/`);
    return data;
  },
  async fetchDeepResearch(reportId: string) {
    const { data } = await api.get<DeepResearchReport>(`/reports/${reportId}/deep-research/`);
    return data;
  },
  async fetchAdminSummary() {
    const { data } = await api.get<AdminSummaryResponse>('/reports/admin/summary/');
    return data;
  }
};

export default api;
